<template>
  <div class="tag-picker" :class="rootClass">
    <div class="tag-picker__workspace">
      <div class="tag-picker__toolbarRow">
        <div class="tag-picker__toolbar tag-picker__toolbar--inline">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索一级标签"
            size="small"
            clearable
            class="tag-picker__search"
            prefix-icon="el-icon-search"
            @input="debounceSearch"
          />
        </div>

        <div
          class="tag-picker__selectedDock tag-picker__selectedDock--inline"
          :class="{ 'tag-picker__selectedDock--empty': !selectedGroup }"
        >
          <i class="el-icon-price-tag" aria-hidden="true" />
          <span class="tag-picker__selectedDockText">
            <template v-if="selectedGroup">
              已选一级标签 · {{ selectedGroup.name }}
            </template>
            <template v-else>点击下方卡片选择所属一级标签</template>
          </span>
          <button
            v-if="selectedGroup && allowClear"
            type="button"
            class="tag-picker__selectedDockAction"
            @click="clearSelection"
          >
            清除
          </button>
        </div>
      </div>

      <div class="tag-picker__scrollWrap">
        <div class="tag-picker__scroll" @wheel="handleScrollWheel">
          <div v-if="filteredGroups.length" class="tag-picker__l1">
            <div class="tag-picker__cardGrid" :class="{ 'tag-picker__cardGrid--dialog': !fullHeight }">
              <button
                v-for="group in filteredGroups"
                :key="group.id"
                type="button"
                class="tag-picker__l1Card"
                :class="{ 'tag-picker__l1Card--selected': isSelected(group) }"
                @click="selectGroup(group)"
              >
                <span class="tag-picker__l1CardTop">
                  <span class="tag-picker__l1CardKind">一级</span>
                  <span v-if="isSelected(group)" class="tag-picker__l1CardBadge">已选</span>
                </span>
                <span class="tag-picker__l1CardName">{{ group.name }}</span>
                <span v-if="childCount(group)" class="tag-picker__l1CardCount">
                  {{ childCount(group) }} 个二级标签
                </span>
                <span v-else class="tag-picker__l1CardCount">暂无二级标签</span>
              </button>
            </div>
          </div>
          <el-empty
            v-else-if="hasLoaded"
            description="未找到匹配的一级标签"
            :image-size="48"
            class="tag-picker__empty"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nestedAll } from '../../../api/tagManage'

/**
 * 标签表单 · 所属一级标签单选器。
 * 视觉与 batchTag/tagPicker 一级卡片墙一致，供二级标签创建/编辑时选择父级。
 */
export default {
  name: 'TagParentCardPicker',
  props: {
    value: {
      type: [Number, String],
      default: '',
    },
    allowClear: {
      type: Boolean,
      default: false,
    },
    /** 大弹窗内占满剩余高度，与批量打标 tagPicker 一致 */
    fullHeight: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchKeyword: '',
      allGroups: [],
      hasLoaded: false,
      timer: null,
    }
  },
  computed: {
    rootClass() {
      return this.fullHeight ? 'tag-picker--fullHeight' : 'tag-picker--dialogEmbed'
    },
    filteredGroups() {
      const keyword = this.searchKeyword.trim().toLowerCase()
      if (!keyword) {
        return this.allGroups
      }
      return this.allGroups.filter((group) => {
        if (String(group.name || '').toLowerCase().includes(keyword)) {
          return true
        }
        return (group.tagVOList || []).some((child) =>
          String(child.name || '').toLowerCase().includes(keyword)
        )
      })
    },
    selectedGroup() {
      const selectedId = Number(this.value)
      if (!Number.isFinite(selectedId) || selectedId <= 0) {
        return null
      }
      return this.allGroups.find((group) => Number(group.id) === selectedId) || null
    },
    hasOptions() {
      return this.allGroups.length > 0
    },
  },
  created() {
    this.loadGroups()
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  methods: {
    loadGroups() {
      nestedAll(this.searchKeyword)
        .then((res) => {
          if (res.success) {
            this.allGroups = res.data || []
          }
        })
        .finally(() => {
          this.hasLoaded = true
        })
    },
    debounceSearch() {
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.loadGroups()
      }, 200)
    },
    childCount(group) {
      return (group.tagVOList || []).length
    },
    isSelected(group) {
      return Number(this.value) === Number(group.id)
    },
    selectGroup(group) {
      const nextId = Number(group.id)
      if (!Number.isFinite(nextId) || nextId <= 0) {
        return
      }
      this.$emit('input', nextId)
      this.$emit('change', nextId)
    },
    clearSelection() {
      this.$emit('input', '')
      this.$emit('change', '')
    },
    handleScrollWheel(event) {
      const el = event.currentTarget
      if (!el || el.scrollHeight <= el.clientHeight) {
        return
      }
      event.stopPropagation()
    },
  },
}
</script>

<style src="../../resourceManage/genericResourceManage/association/pickerShared.css"></style>
<style scoped>
.tag-picker__toolbarRow {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-bottom: 8px;
}

.tag-picker__toolbar--inline {
  flex: 0 0 240px;
  width: 240px;
  max-width: 36%;
  margin-bottom: 0;
}

.tag-picker__selectedDock--inline {
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: 0;
}

.tag-picker--fullHeight {
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tag-picker--fullHeight .tag-picker__workspace {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tag-picker--fullHeight .tag-picker__scrollWrap {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  overflow: hidden;
}

.tag-picker--fullHeight .tag-picker__scroll {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.tag-picker--dialogEmbed {
  height: auto;
}

.tag-picker--dialogEmbed .tag-picker__workspace {
  height: auto;
  min-height: 0;
  padding: 10px;
}

.tag-picker--dialogEmbed .tag-picker__scrollWrap {
  height: auto;
  min-height: 0;
}

.tag-picker--dialogEmbed .tag-picker__scroll {
  flex: none;
  max-height: 240px;
  min-height: 120px;
}

.tag-picker--dialogEmbed .tag-picker__cardGrid--dialog {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 720px) {
  .tag-picker__toolbarRow {
    flex-direction: column;
    align-items: stretch;
  }

  .tag-picker__toolbar--inline {
    flex: none;
    width: 100%;
    max-width: none;
  }
}

@media (max-width: 480px) {
  .tag-picker--dialogEmbed .tag-picker__cardGrid--dialog {
    grid-template-columns: 1fr;
  }
}
</style>
